import { useState, useEffect, useRef, useMemo } from 'react';
import { useApp } from '../context/AppContext';

const _NOW      = new Date();
const TODAY     = _NOW.getDate();
const CAL_YEAR  = _NOW.getFullYear();
const CAL_MONTH = _NOW.getMonth();   // 0-indexed
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function dayNum(off, i) { return TODAY + off + i; }
function toDate(d) { return new Date(CAL_YEAR, CAL_MONTH, d); }

// Convert ISO datetime string → calendar day integer (same coordinate system as dayNum)
function isoToDayInt(isoStr) {
  if (!isoStr) return 0;
  const d = new Date(isoStr);
  d.setHours(0, 0, 0, 0);
  const ref = new Date(CAL_YEAR, CAL_MONTH, 1);
  return Math.round((d - ref) / 86400000) + 1;
}

// Number of days between two ISO strings (inclusive both start and end day)
function calcSpan(startISO, endISO) {
  if (!startISO || !endISO) return 1;
  const s = new Date(startISO); s.setHours(0, 0, 0, 0);
  const e = new Date(endISO);   e.setHours(0, 0, 0, 0);
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
}

export default function CalendarPage() {
  const { vehicles, rentals, setRentals, openModal, showToast, setRentalPreset, setCalDetail, setRentalDraft, vehicleTypes, maintHist, users, isOperatorRole, currentUserId } = useApp();
  const [calOff, setCalOff]               = useState(0);
  const [conflictVisible, setConflictVisible] = useState(false);
  const [moveConfirm, setMoveConfirm]     = useState(null);
  const [resizeConfirm, setResizeConfirm] = useState(null);
  const [createDrag, setCreateDrag]       = useState(null);
  const [resizeDrag, setResizeDrag]       = useState(null);
  const [customDays, setCustomDays] = useState(30);
  const [rangeSelStart, setRangeSelStart] = useState(null);
  const [rangeSelHover, setRangeSelHover] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => new Date(_NOW.getFullYear(), _NOW.getMonth(), 1));
  const [pickStart, setPickStart] = useState(null);  // Date object
  const [pickEnd, setPickEnd] = useState(null);       // Date object
  const [pickHover, setPickHover] = useState(null);   // Date object

  const N     = customDays;
  const COL_W = 88;
  const startDay = dayNum(calOff, 0);
  const endDay   = dayNum(calOff, N - 1);

  // ── Single source of truth: derive calendar blocks from rentals ──────────────
  const isOp = isOperatorRole();

  const calBlocks = useMemo(() => {
    const now = new Date();
    const rentalBlocks = rentals
      .filter(r => ['booked', 'active', 'overdue'].includes(r.status) && r.start && r.end)
      .filter(r => !isOp || r.assignedTo === currentUserId)
      .map(r => {
        const isOverdue = (r.status === 'active' || r.status === 'overdue') && new Date(r.end) < now;
        const cls  = isOverdue ? 'cb-o' : r.status === 'active' ? 'cb-r' : 'cb-b';
        const type = isOverdue ? 'overdue' : r.status === 'active' ? 'rental' : 'booking';
        const src      = r.source === 'online' ? ' (Online)' : '';
        const times = [];
        if (r.t1) times.push(`↑${r.t1}`);
        if (r.t2) times.push(`↓${r.t2}`);
        const timeInfo = times.length ? ` · ${times.join(' ')}` : '';
        const locInfo = r.pickupLocName ? ` · 📍 ${r.pickupLocName}` : '';
        const assignUser = r.assignedTo ? users.find(u => u.id === r.assignedTo) : null;
        const assignInfo = assignUser ? ` · 👤 ${assignUser.fname}` : ' · ⚠️';
        return {
          id:       r.id,
          vid:      r.vid,
          start:    isoToDayInt(r.start),
          span:     calcSpan(r.start, r.end),
          label:    `${r.customer || ''}${src}${timeInfo}${locInfo}${assignInfo}`,
          cls,
          type,
          rentalType: r.type || 'rental',  // 'rental' | 'lease' — used by End button routing
          rentalId: r.id,
          assignedTo: r.assignedTo || null,
        };
      });

    // Add maintenance blocks — in_progress maintenance spans from today onward
    const maintBlocks = maintHist
      .filter(m => m.status === 'in_progress')
      .map(m => {
        const todayInt = isoToDayInt(now.toISOString());
        return {
          id:       `maint-${m.id}`,
          vid:      m.vid,
          start:    todayInt,
          span:     N,
          label:    `🔧 Maintenance · ${m.type || 'Service'}`,
          cls:      'cb-m',
          type:     'maintenance',
          rentalId: null,
        };
      });

    return [...rentalBlocks, ...maintBlocks];
  }, [rentals, maintHist, N, users, isOp, currentUserId]);

  const calBlocksRef  = useRef(calBlocks);
  const createDragRef = useRef(null);
  const scrollRef     = useRef(null);
  const resizeDragRef = useRef(null);
  const moveDragRef   = useRef(null);  // touch-based block-to-cell drag (HTML5 drag-and-drop doesn't fire on touch)
  const longPressRef  = useRef(null);  // { kind, timer, startX, startY, payload } — long-press to engage touch drag (avoids hijacking scroll)
  const LONG_PRESS_MS = 500;
  const LONG_PRESS_MOVE_TOLERANCE = 8; // px — if finger moves more than this before timer fires, it's a scroll

  useEffect(() => { calBlocksRef.current = calBlocks; }, [calBlocks]);

  useEffect(() => {
    createDragRef.current = null;
    setCreateDrag(null);
  }, [calOff]);

  useEffect(() => {
    // Normalise mouse + touch events so the same logic handles both
    function eventPoint(e) {
      const p = e.touches?.[0] || e.changedTouches?.[0] || e;
      return { clientX: p.clientX, clientY: p.clientY };
    }

    function onMouseMove(e) {
      // Long-press tracking: if finger moves past tolerance before the timer fires, it's a scroll — cancel
      if (longPressRef.current && e.touches) {
        const { clientX, clientY } = eventPoint(e);
        const dx = clientX - longPressRef.current.startX;
        const dy = clientY - longPressRef.current.startY;
        if (dx * dx + dy * dy > LONG_PRESS_MOVE_TOLERANCE * LONG_PRESS_MOVE_TOLERANCE) {
          clearTimeout(longPressRef.current.timer);
          longPressRef.current = null;
        }
      }
      if (createDragRef.current) {
        const { clientX, clientY } = eventPoint(e);
        const el   = document.elementFromPoint(clientX, clientY);
        const cell = el?.closest?.('[data-cal-cell]');
        if (cell) {
          const day = parseInt(cell.dataset.day);
          if (!isNaN(day)) {
            const sd = createDragRef.current.startDay;
            const ed = Math.max(sd, day);
            if (ed !== createDragRef.current.endDay) {
              createDragRef.current.endDay = ed;
              setCreateDrag(d => d ? { ...d, endDay: ed } : d);
            }
          }
        }
        // Prevent the browser from scrolling/zooming while we drag on touch
        if (e.touches) e.preventDefault();
        return;
      }
      if (resizeDragRef.current) {
        const { bid, startX, cellWidth, originalSpan } = resizeDragRef.current;
        const { clientX } = eventPoint(e);
        const delta   = Math.round((clientX - startX) / cellWidth);
        const newSpan = Math.max(1, originalSpan + delta);
        if (newSpan !== resizeDragRef.current.currentSpan) {
          resizeDragRef.current.currentSpan = newSpan;
          setResizeDrag({ bid, currentSpan: newSpan });
        }
        if (e.touches) e.preventDefault();
      }
      // Touch-only: block move (mouse uses HTML5 drag-and-drop which fires onDragStart/Drop)
      if (moveDragRef.current && e.touches) {
        const { clientX, clientY } = eventPoint(e);
        // Detect threshold to enter "moving" state — avoid hijacking a tap
        if (!moveDragRef.current.moving) {
          const dx = clientX - moveDragRef.current.startX;
          const dy = clientY - moveDragRef.current.startY;
          if (dx * dx + dy * dy > 100) moveDragRef.current.moving = true;
        }
        if (moveDragRef.current.moving) {
          e.preventDefault();
          const el   = document.elementFromPoint(clientX, clientY);
          const cell = el?.closest?.('[data-cal-cell]');
          if (cell) {
            moveDragRef.current.targetVid = cell.dataset.vid;
            moveDragRef.current.targetDay = parseInt(cell.dataset.day);
          }
        }
      }
    }

    function onMouseUp() {
      // Cancel any pending long-press timer (tap released before drag engaged)
      if (longPressRef.current) {
        clearTimeout(longPressRef.current.timer);
        longPressRef.current = null;
      }
      if (createDragRef.current) {
        const { vid, startDay: sd, endDay: ed } = createDragRef.current;
        createDragRef.current = null;
        setCreateDrag(null);
        const pad = n => String(n).padStart(2, '0');
        const fmt = (y, m, d) => `${y}-${pad(m)}-${pad(d)}T09:00`;
        const s  = toDate(sd);
        const e2 = toDate(ed + 1);
        setRentalPreset({
          vid,
          start: fmt(s.getFullYear(), s.getMonth() + 1, s.getDate()),
          end:   fmt(e2.getFullYear(), e2.getMonth() + 1, e2.getDate()),
        });
        openModal('add-rental', vid);
        return;
      }
      if (resizeDragRef.current) {
        const { bid, originalSpan, currentSpan } = resizeDragRef.current;
        resizeDragRef.current = null;
        if (currentSpan === undefined || currentSpan === originalSpan) {
          setResizeDrag(null);
          return;
        }
        const b = calBlocksRef.current.find(x => x.id === bid);
        setResizeDrag(null);
        // Extending an active/overdue rental → route through the Extend flow so the
        // operator confirms/adjusts the price and a pro forma is created.
        if (b && (b.type === 'rental' || b.type === 'overdue') && currentSpan > originalSpan) {
          const endDt = toDate(b.start + currentSpan - 1);
          const pad = n => String(n).padStart(2, '0');
          const ymd = `${endDt.getFullYear()}-${pad(endDt.getMonth() + 1)}-${pad(endDt.getDate())}`;
          openModal('extend-rental', { vid: b.vid, newEnd: ymd });
          return;
        }
        setResizeConfirm({ bid, oldSpan: originalSpan, newSpan: currentSpan, label: b?.label || bid });
      }
      if (moveDragRef.current) {
        const { bid, targetVid, targetDay, moving } = moveDragRef.current;
        moveDragRef.current = null;
        if (!moving || targetVid == null || targetDay == null) return;  // just a tap
        const b = calBlocksRef.current.find(x => x.id === bid);
        if (!b) return;
        // Same conflict check as handleDrop
        const conflict = calBlocksRef.current.some(x =>
          x.id !== bid && x.vid === targetVid &&
          targetDay < x.start + x.span && targetDay + b.span > x.start
        );
        if (conflict) { setConflictVisible(true); return; }
        setMoveConfirm({ bid, targetVid, targetDay, label: b.label, oldVid: b.vid });
      }
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
    // Touch equivalents — passive:false so preventDefault works while dragging
    document.addEventListener('touchmove', onMouseMove, { passive: false });
    document.addEventListener('touchend',  onMouseUp);
    document.addEventListener('touchcancel', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onMouseUp);
      document.removeEventListener('touchmove', onMouseMove);
      document.removeEventListener('touchend',  onMouseUp);
      document.removeEventListener('touchcancel', onMouseUp);
    };
  }, [openModal, setRentalPreset]);

  function handleCellMouseDown(e, vid, day) {
    // Accept left mouse button OR any touch event
    if (e.button !== undefined && e.button !== 0) return;
    const occupied = calBlocksRef.current.some(b => {
      if (b.vid !== vid) return false;
      // Allow clicking on the last day (return day) of multi-day blocks — it's the half-day handover
      const lastDay = b.start + b.span - 1;
      if (b.span > 1 && day === lastDay) return false;
      return day >= b.start && day < b.start + b.span;
    });
    if (occupied) return;

    const isTouch = !!e.touches;
    if (isTouch) {
      // Long-press to engage drag so casual scroll doesn't hijack
      const t = e.touches[0];
      // Clear any previous long-press
      if (longPressRef.current?.timer) clearTimeout(longPressRef.current.timer);
      longPressRef.current = {
        kind: 'create',
        startX: t.clientX, startY: t.clientY,
        timer: setTimeout(() => {
          createDragRef.current = { vid, startDay: day, endDay: day };
          setCreateDrag({ vid, startDay: day, endDay: day });
          try { if (window.navigator?.vibrate) window.navigator.vibrate(15); } catch (err) {}
          longPressRef.current = null;
        }, LONG_PRESS_MS),
      };
      return;
    }
    // Mouse: keep instant
    if (e.cancelable !== false) e.preventDefault();
    createDragRef.current = { vid, startDay: day, endDay: day };
    setCreateDrag({ vid, startDay: day, endDay: day });
  }

  function handleResizeStart(e, bid, span, cellWidth) {
    e.stopPropagation();
    if (e.cancelable !== false) e.preventDefault();
    const point = e.touches?.[0] || e;
    resizeDragRef.current = { bid, startX: point.clientX, cellWidth, originalSpan: span, currentSpan: span };
    setResizeDrag({ bid, currentSpan: span });
  }

  function handleDrop(e, targetVid, targetDay) {
    e.preventDefault();
    const bid = e.dataTransfer.getData('bid');
    if (!bid) return;
    const b = calBlocksRef.current.find(x => x.id === bid);
    if (!b) return;
    const conflict = calBlocksRef.current.some(x =>
      x.id !== bid && x.vid === targetVid &&
      targetDay < x.start + x.span && targetDay + b.span > x.start
    );
    if (conflict) { setConflictVisible(true); return; }
    setMoveConfirm({ bid, targetVid, targetDay, label: b.label, oldVid: b.vid });
  }

  const pad      = n => String(n).padStart(2, '0');
  const fmtISO   = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T09:00`;
  const fmtLabel = d => d.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });

  function confirmMove() {
    const { bid, targetVid, targetDay } = moveConfirm;
    const b       = calBlocksRef.current.find(x => x.id === bid);
    const span    = b?.span || 1;
    const startDt = toDate(targetDay);
    const endDt   = toDate(targetDay + span - 1);
    setRentals(prev => prev.map(r => r.id === bid
      ? { ...r, vid: targetVid,
          start:      fmtISO(startDt), startLabel: fmtLabel(startDt),
          end:        fmtISO(endDt),   endLabel:   fmtLabel(endDt) }
      : r
    ));
    showToast('✅ Booking moved');
    setMoveConfirm(null);
  }

  function confirmResize() {
    const { bid, newSpan } = resizeConfirm;
    const b     = calBlocksRef.current.find(x => x.id === bid);
    const endDt = toDate((b?.start || 0) + newSpan - 1);
    setRentals(prev => prev.map(r => r.id === bid
      ? { ...r, end: fmtISO(endDt), endLabel: fmtLabel(endDt) }
      : r
    ));
    showToast(`✅ Booking resized to ${newSpan} day${newSpan > 1 ? 's' : ''}`);
    setResizeConfirm(null);
  }

  function cancelResize() {
    setResizeConfirm(null); // blocks re-derive from unchanged rentals automatically
  }

  function prev()    { setCalOff(o => o - 7); }
  function next()    { setCalOff(o => o + 7); }
  function goToday() { setCalOff(0); }

  return (
    <div>
      {/* Conflict bar */}
      {conflictVisible && (
        <div className="conflict-bar" style={{ display: 'flex' }}>
          ⚠️ <strong>Conflict!</strong> &nbsp;Vehicle already booked for those dates.
          <button className="btn btn-sm" style={{ background: '#fff', color: '#7A1E00', border: 'none', marginLeft: 4 }} onClick={() => setConflictVisible(false)}>Dismiss</button>
        </div>
      )}

      {/* Move confirmation */}
      {moveConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--sur)', borderRadius: 'var(--r)', padding: '20px 24px', minWidth: 300, boxShadow: '0 8px 32px rgba(0,0,0,.25)' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Confirm Move</div>
            <div style={{ fontSize: 11, color: 'var(--tx2)', marginBottom: 16 }}>
              Move <strong>{moveConfirm.label}</strong> to {toDate(moveConfirm.targetDay).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}
              {moveConfirm.targetVid !== moveConfirm.oldVid && ' on a different vehicle'}?
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setMoveConfirm(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={confirmMove}>Move</button>
            </div>
          </div>
        </div>
      )}

      {/* Resize confirmation */}
      {resizeConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--sur)', borderRadius: 'var(--r)', padding: '20px 24px', minWidth: 300, boxShadow: '0 8px 32px rgba(0,0,0,.25)' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Confirm Resize</div>
            <div style={{ fontSize: 11, color: 'var(--tx2)', marginBottom: 16 }}>
              Resize <strong>{resizeConfirm.label}</strong> from {resizeConfirm.oldSpan} to {resizeConfirm.newSpan} day{resizeConfirm.newSpan !== 1 ? 's' : ''}?
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={cancelResize}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={confirmResize}>Resize</button>
            </div>
          </div>
        </div>
      )}

      <div className="tb2" style={{ marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={prev}>‹</button>
        <div style={{ position: 'relative' }}>
          <span
            style={{ fontSize: 12, fontWeight: 500, minWidth: 150, textAlign: 'center', cursor: 'pointer', padding: '4px 10px', borderRadius: 6, border: '1px solid var(--bd)', background: 'var(--sur)', display: 'inline-block' }}
            onClick={() => {
              setPickStart(null); setPickEnd(null); setPickHover(null);
              setPickerMonth(new Date(toDate(startDay).getFullYear(), toDate(startDay).getMonth(), 1));
              setShowPicker(p => !p);
            }}
          >
            {toDate(startDay).toLocaleDateString('en-GB',{day:'numeric',month:'short'})} –{' '}
            {toDate(endDay).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
            <span style={{ fontSize: 10, color: 'var(--tx3)', marginLeft: 6 }}>({N}d)</span>
          </span>

          {/* ── Custom calendar range picker ── */}
          {showPicker && (() => {
            const pm = pickerMonth;
            const pmYear = pm.getFullYear();
            const pmMonth = pm.getMonth();
            const firstDay = new Date(pmYear, pmMonth, 1).getDay(); // 0=Sun
            const daysInMonth = new Date(pmYear, pmMonth + 1, 0).getDate();
            const startPad = (firstDay + 6) % 7; // Mon=0
            const monthLabel = pm.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

            // Next month for 2-month view
            const nm = new Date(pmYear, pmMonth + 1, 1);
            const nmYear = nm.getFullYear();
            const nmMonth = nm.getMonth();
            const nmFirstDay = nm.getDay();
            const nmDaysInMonth = new Date(nmYear, nmMonth + 1, 0).getDate();
            const nmStartPad = (nmFirstDay + 6) % 7;
            const nmLabel = nm.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

            const sameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
            const effEnd = pickEnd || pickHover;
            const rMin = pickStart && effEnd ? (pickStart < effEnd ? pickStart : effEnd) : null;
            const rMax = pickStart && effEnd ? (pickStart > effEnd ? pickStart : effEnd) : null;
            const inRange = (dt) => rMin && rMax && dt >= rMin && dt <= rMax;
            const rangeDays = rMin && rMax ? Math.round((rMax - rMin) / 86400000) + 1 : 0;

            function handleDayClick(dt) {
              if (!pickStart || pickEnd) {
                // First click or restart
                setPickStart(dt);
                setPickEnd(null);
                setPickHover(null);
              } else {
                // Second click — apply
                const s = dt < pickStart ? dt : pickStart;
                const e = dt > pickStart ? dt : pickStart;
                const days = Math.round((e - s) / 86400000) + 1;
                const ref = new Date(CAL_YEAR, CAL_MONTH, TODAY);
                ref.setHours(0,0,0,0);
                s.setHours(0,0,0,0);
                setCalOff(Math.round((s - ref) / 86400000));
                setCustomDays(Math.max(7, Math.min(days, 365)));
                setPickStart(null); setPickEnd(null); setPickHover(null);
                setShowPicker(false);
              }
            }

            function renderMonth(year, month, daysIn, pad, label) {
              return (
                <div>
                  <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--tx)' }}>{label}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 32px)', gap: 1, justifyContent: 'center' }}>
                    {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => (
                      <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 600, color: 'var(--tx3)', padding: '2px 0' }}>{d}</div>
                    ))}
                    {Array.from({ length: pad }, (_, i) => <div key={'p'+i} />)}
                    {Array.from({ length: daysIn }, (_, i) => {
                      const dt = new Date(year, month, i + 1);
                      const isStart = sameDay(dt, pickStart);
                      const isEnd = sameDay(dt, effEnd);
                      const isIn = inRange(dt);
                      const isToday = dt.toDateString() === _NOW.toDateString();
                      return (
                        <div
                          key={i}
                          onClick={() => handleDayClick(new Date(year, month, i + 1))}
                          onMouseEnter={() => { if (pickStart && !pickEnd) setPickHover(new Date(year, month, i + 1)); }}
                          style={{
                            textAlign: 'center', fontSize: 11, padding: '5px 0', cursor: 'pointer',
                            borderRadius: (isStart || isEnd) ? 6 : 0,
                            background: (isStart || isEnd) ? 'var(--ac)' : isIn ? 'rgba(196,67,52,.12)' : 'transparent',
                            color: (isStart || isEnd) ? '#fff' : isToday ? 'var(--ac)' : 'var(--tx)',
                            fontWeight: (isStart || isEnd || isToday) ? 700 : 400,
                          }}
                        >
                          {i + 1}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <div style={{
                position: 'absolute', top: '110%', left: 0,
                background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 10,
                boxShadow: 'var(--sh2)', padding: 16, zIndex: 100,
              }}
                onClick={e => e.stopPropagation()}
              >
                {/* Nav */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPickerMonth(new Date(pmYear, pmMonth - 1, 1))}>‹</button>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)' }}>
                    {pickStart && !pickEnd ? 'Now pick end date' : 'Pick start date'}
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPickerMonth(new Date(pmYear, pmMonth + 1, 1))}>›</button>
                </div>

                {/* Two month grid */}
                <div style={{ display: 'flex', gap: 16 }}>
                  {renderMonth(pmYear, pmMonth, daysInMonth, startPad, monthLabel)}
                  {renderMonth(nmYear, nmMonth, nmDaysInMonth, nmStartPad, nmLabel)}
                </div>

                {/* Range info */}
                {pickStart && (
                  <div style={{
                    marginTop: 10, padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, textAlign: 'center',
                    background: 'var(--ac-l)', color: 'var(--ac)', border: '1px solid var(--ac-b)',
                  }}>
                    {pickStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {effEnd && !sameDay(pickStart, effEnd) && (
                      <> → {effEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · {rangeDays} days</>
                    )}
                  </div>
                )}

                {/* Quick buttons */}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 10 }}
                    onClick={() => { setCalOff(0); setCustomDays(30); setShowPicker(false); }}>Today (30d)</button>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 10 }}
                    onClick={() => { setCalOff(0); setCustomDays(60); setShowPicker(false); }}>60d</button>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 10 }}
                    onClick={() => { setCalOff(0); setCustomDays(90); setShowPicker(false); }}>90d</button>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 10, color: 'var(--ac)' }}
                    onClick={() => { setPickStart(null); setPickEnd(null); setPickHover(null); setShowPicker(false); }}>✕ Close</button>
                </div>
              </div>
            );
          })()}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={next}>›</button>
        <button className="btn btn-ghost btn-sm" onClick={() => { setCalOff(0); setCustomDays(30); }}>Today</button>
        <select className="fsel" style={{ marginLeft: 'auto' }}>
          <option value="">All vehicles</option>
          {(vehicleTypes || []).map(t => (
            <option key={t.id} value={t.name}>{t.icon} {t.name}</option>
          ))}
        </select>
        <button className="btn btn-primary btn-sm" onClick={() => openModal('add-rental')}>+ Booking</button>
      </div>

      <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 8, padding: '6px 10px', background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--r)' }}>
        <strong style={{ color: 'var(--tx2)' }}>Drag</strong> block to move &nbsp;·&nbsp;
        <strong style={{ color: 'var(--tx2)' }}>Drag right edge</strong> to resize &nbsp;·&nbsp;
        <strong style={{ color: 'var(--tx2)' }}>Click</strong> block to view &nbsp;·&nbsp;
        <strong style={{ color: 'var(--gr)' }}>Click / drag empty cell</strong> to create booking
      </div>

      <div className="cal-wrap">
        <div className="cal-scroll" ref={scrollRef}
          onWheel={e => {
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
            e.preventDefault();
            scrollRef.current.scrollLeft += e.deltaY * 1.2;
          }}
        >
          <div className="cal-g" style={{ minWidth: 150 + N * COL_W }}>
            {/* Header row */}
            <div className="cal-hrow" style={{ gridTemplateColumns: `150px repeat(${N},${COL_W}px)` }}>
              <div className="cal-ahd" style={{ position: 'sticky', left: 0, zIndex: 10, background: 'var(--sur)' }}>Assets</div>
              {Array.from({ length: N }, (_, i) => {
                const d   = dayNum(calOff, i);
                const dt  = toDate(d);
                const dn  = DAYS[dt.getDay()];
                const isT = dt.toDateString() === _NOW.toDateString();
                const showMonth = i === 0 || dt.getDate() === 1;
                const monthLabel = dt.toLocaleDateString('en-GB', { month: 'short' });
                const yearLabel = dt.getFullYear();
                return (
                  <div key={i} className={`cal-dhd${isT ? ' td' : ''}`}>
                    {showMonth && (
                      <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--ac)', textTransform: 'uppercase', letterSpacing: '.05em', lineHeight: 1 }}>
                        {monthLabel} {yearLabel !== CAL_YEAR || i === 0 ? yearLabel : ''}
                      </div>
                    )}
                    <div className="cal-dn">{dn}</div>
                    <div className="cal-dd">{dt.getDate()}</div>
                  </div>
                );
              })}
            </div>

            {/* Vehicle rows */}
            {vehicles.map(v => (
              <div key={v.id} className="cal-vrow" style={{ gridTemplateColumns: `150px repeat(${N},${COL_W}px)` }}>
                <div className="cal-vi" style={{ position: 'sticky', left: 0, zIndex: 10, background: 'var(--sur)' }}>
                  <div className="cal-vn">{v.icon} {v.name.split('–')[0].trim()}</div>
                  <div className="cal-vp">{v.plate}</div>
                </div>
                {Array.from({ length: N }, (_, i) => {
                  const day        = dayNum(calOff, i);
                  const dayIsToday = toDate(day).toDateString() === _NOW.toDateString();
                  const blockHere  = calBlocks.find(b => {
                    if (b.vid !== v.id) return false;
                    if (b.start === day) return true;
                    // Block started before visible range — render at first visible cell
                    if (b.start < startDay && day === startDay && b.start + b.span > startDay) return true;
                    return false;
                  });
                  const occupied = calBlocks.some(b => {
                    if (b.vid !== v.id) return false;
                    const lastDay = b.start + b.span - 1;
                    if (b.span > 1 && day === lastDay) return false; // return day = available half
                    return day > b.start && day < b.start + b.span;
                  });

                  // Is this day only the return-day half of a block? Show crosshair
                  const isReturnDayOnly = !blockHere && calBlocks.some(b => b.vid === v.id && b.span > 1 && day === b.start + b.span - 1);

                  const isCreateStart = createDrag && createDrag.vid === v.id && day === createDrag.startDay;
                  const createSpan    = createDrag
                    ? Math.min(createDrag.endDay - createDrag.startDay + 1, N - (createDrag.startDay - startDay))
                    : 1;

                  return (
                    <div
                      key={i}
                      className={`cal-cell${dayIsToday ? ' drt' : ''}`}
                      data-cal-cell
                      data-vid={v.id}
                      data-day={day}
                      style={{ position: 'relative', cursor: (occupied || (blockHere && !isReturnDayOnly)) ? 'default' : 'crosshair' }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => handleDrop(e, v.id, day)}
                      onMouseDown={e => handleCellMouseDown(e, v.id, day)}
                      onTouchStart={e => handleCellMouseDown(e, v.id, day)}
                    >
                      {blockHere && (() => {
                        // Last day of every block = half width (return day), so it's clear the vehicle can be rented again
                        const blockEnd = blockHere.start + blockHere.span - 1;
                        const prevBlock = calBlocks.find(ob => ob.id !== blockHere.id && ob.vid === v.id && (ob.start + ob.span - 1) === blockHere.start);
                        const trimRight = blockHere.span > 1; // always trim last day (except single-day blocks)
                        const trimLeft  = !!prevBlock;
                        return (
                          <CalBlock
                            key={blockHere.id}
                            block={blockHere}
                            visibleStart={startDay}
                            N={N}
                            openModal={openModal}
                            setCalDetail={setCalDetail}
                            trimRight={trimRight}
                            trimLeft={trimLeft}
                            resizeSpan={resizeDrag?.bid === blockHere.id ? resizeDrag.currentSpan : undefined}
                            onResizeStart={(e, cw) => handleResizeStart(e, blockHere.id, blockHere.span, cw)}
                            onTouchMoveStart={(touch) => {
                              // Long-press to engage the move (lets the user scroll the page first)
                              if (longPressRef.current?.timer) clearTimeout(longPressRef.current.timer);
                              longPressRef.current = {
                                kind: 'move',
                                startX: touch.clientX, startY: touch.clientY,
                                timer: setTimeout(() => {
                                  moveDragRef.current = { bid: blockHere.id, startX: touch.clientX, startY: touch.clientY, moving: true, targetVid: null, targetDay: null };
                                  try { if (window.navigator?.vibrate) window.navigator.vibrate(15); } catch (err) {}
                                  longPressRef.current = null;
                                }, LONG_PRESS_MS),
                              };
                            }}
                            onStartBooking={b => {
                              const rental = rentals.find(r => r.id === b.id);
                              if (rental) { setRentalDraft(rental); openModal('check-in'); }
                              else { openModal('add-rental', b.vid); }
                            }}
                          />
                        );
                      })()}
                      {isCreateStart && !blockHere && (
                        <div style={{
                          position: 'absolute', top: 3, bottom: 3, left: 3,
                          width: `calc(${createSpan * 100}% - 6px)`,
                          borderRadius: 5,
                          background: 'rgba(26,122,74,.2)',
                          border: '2px dashed var(--gr)',
                          zIndex: 3, pointerEvents: 'none',
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="cal-leg" style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 10, color: 'var(--tx2)' }}>
        <div className="cl-it" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--ac)' }} />Active Rental
        </div>
        <div className="cl-it" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--am-l)', border: '1px solid var(--am-b)' }} />Booking
        </div>
        <div className="cl-it" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#7A1E00' }} />Overdue
        </div>
        <div className="cl-it" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--pu-l)', border: '1px solid var(--pu-b)' }} />Maintenance
        </div>
      </div>
    </div>
  );
}

function CalBlock({ block: b, visibleStart, N, openModal, setCalDetail, resizeSpan, onResizeStart, onTouchMoveStart, onStartBooking, trimRight, trimLeft }) {
  const resizeActiveRef = useRef(false);

  const clipOffset  = Math.max(0, visibleStart - b.start);
  const displaySpan = (resizeSpan !== undefined ? resizeSpan : b.span) - clipOffset;
  const span = Math.min(displaySpan, N - Math.max(0, b.start - visibleStart));
  if (span <= 0) return null;

  // Half-day trim for same-day handover
  const trimR = trimRight ? 50 : 0; // trim right by half a column (%)
  const trimL = trimLeft  ? 50 : 0; // trim left by half a column (%)

  function handleResizeMouseDown(e) {
    e.stopPropagation();
    if (e.cancelable !== false) e.preventDefault();
    resizeActiveRef.current = true;
    const cell = e.currentTarget.closest('[data-cal-cell]');
    const cellWidth = cell ? cell.getBoundingClientRect().width : 100;
    onResizeStart(e, cellWidth);
    function onUp() {
      resizeActiveRef.current = false;
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
      document.removeEventListener('touchcancel', onUp);
    }
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    document.addEventListener('touchcancel', onUp);
  }

  return (
    <div
      className={`cal-blk ${b.cls}`}
      draggable={b.type !== 'maintenance'}
      onMouseDown={e => e.stopPropagation()}
      onTouchStart={e => {
        e.stopPropagation();
        if (b.type === 'maintenance' || resizeActiveRef.current) return;
        const t = e.touches[0];
        onTouchMoveStart && onTouchMoveStart({ clientX: t.clientX, clientY: t.clientY });
      }}
      onDragStart={e => {
        if (b.type === 'maintenance') { e.preventDefault(); return; }
        if (resizeActiveRef.current) { e.preventDefault(); return; }
        e.dataTransfer.setData('bid', b.id);
      }}
      onClick={e => { e.stopPropagation(); if (b.type !== 'maintenance') setCalDetail({ open: true, bid: b.id }); }}
      style={{
        position: 'absolute', top: 3, bottom: 3,
        left: trimL ? `calc(50% + 2px)` : 3,
        width: trimL && trimR
          ? `calc(${span * 100}% - ${50}% - ${50}% + 0px)`
          : trimL
            ? `calc(${span * 100}% - 50% - 4px)`
            : trimR
              ? `calc(${span * 100}% - 50% - 4px)`
              : `calc(${span * 100}% - 6px)`,
        borderRadius: 5, display: 'flex', alignItems: 'center',
        padding: '0 6px', fontSize: 9, fontWeight: 600,
        cursor: 'grab', overflow: 'hidden', whiteSpace: 'nowrap',
        userSelect: 'none', zIndex: 2,
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{b.label}</span>
      {b.type === 'booking' && (
        <button
          className="cal-blk-btn cal-blk-start"
          onClick={e => { e.stopPropagation(); onStartBooking ? onStartBooking(b) : openModal('add-rental', b.vid); }}
          title="Start Rental"
        >▶</button>
      )}
      {(b.type === 'rental' || b.type === 'overdue') && (
        <button
          className="cal-blk-btn cal-blk-end"
          onClick={e => { e.stopPropagation(); openModal(b.rentalType === 'lease' ? 'end-lease' : 'end-rental', b.vid); }}
          title={b.rentalType === 'lease' ? 'End Lease' : 'End Rental'}
        >■</button>
      )}
      {/* Resize handle — not for maintenance blocks */}
      {b.type !== 'maintenance' && (
        <div
          className="rh"
          onMouseDown={handleResizeMouseDown}
          onTouchStart={handleResizeMouseDown}
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', right: 0, top: 0, bottom: 0,
            width: 14,  /* a bit wider so touch fingers can hit it */
            cursor: 'ew-resize',
            background: 'rgba(0,0,0,.18)',
            borderRadius: '0 5px 5px 0',
            flexShrink: 0,
            touchAction: 'none',
          }}
        />
      )}
    </div>
  );
}
